import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useContentQueryApi } from "@/composables/useContentQueryApi";
import type { ExperienceMedia as ExperienceMediaCQS } from "@/types/generated/ContentQueryApiV2";
import type { ImageV2 as ImageV2CQS } from "@/types/generated/ContentQueryApi";
import type { ExperienceImage } from "@/features/experience-media/types/experience-media-types";

export const EXPERIENCE_MEDIA_QUERY_KEY = "experience-media";

export interface ExperienceMediaQueryData {
  /** The raw media document, used as the base payload when persisting. */
  experienceMedia: ExperienceMediaCQS | undefined;
  /** Gallery images mapped to the editable form shape, sorted by visualization order. */
  gallery: ExperienceImage[];
  /** Experience source (e.g. INTERNAL), needed by the DAM images query. */
  experienceSource: string | undefined;
  /** English title used for the browser tab title. */
  title: string | undefined;
}

function mapPayloadToForm(images?: ImageV2CQS[]): ExperienceImage[] {
  return (
    images?.map((image) => ({
      id: image.id,
      name: image.file_name as string,
      preview_url: image.preview_url,
      visualization_order: image.visualization_order,
    })) ?? []
  );
}

/**
 * Fetches the distribution content for an experience and exposes the media-related
 * slices as reactive query data. This replaces the imperative `loadExperienceMedia`
 * action on the monolithic store and acts as the single source of server data for
 * the media field stores.
 */
export function useExperienceMediaQuery(experienceId: Ref<string | undefined>) {
  const { getDistributionContent } = useContentQueryApi();

  return useQuery({
    queryKey: [EXPERIENCE_MEDIA_QUERY_KEY, experienceId],
    queryFn: async (): Promise<ExperienceMediaQueryData> => {
      if (!experienceId.value) {
        throw new Error("Experience ID is required");
      }
      const { data } = await getDistributionContent(experienceId.value);

      const images = data.experience_media?.images_v2 ?? [];
      const sortedImages = [...images].sort((a, b) => a.visualization_order - b.visualization_order);

      const englishTranslation = data.experience_content?.find((translation) => translation.language_code === "en");

      return {
        experienceMedia: data.experience_media,
        gallery: mapPayloadToForm(sortedImages),
        experienceSource: data.experience_source,
        title: englishTranslation?.experience_translation?.title,
      };
    },
    enabled: computed(() => !!experienceId.value),
  });
}
